<script>
import { onDemandQualityOptions } from '../../../configuration';
import settingsService from '../../../services/settings-service';
import DataLayer from './../../../services/data-layer/index'
import modalService from '../../../services/modal-service'
import messages from '../../../data/messages'

export default {
    page() {
        const title = 'Settings - Audio Quality'
        const description = 'Settings - Audio Quality'
        return {
            title,
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },

                { property: 'twitter:title', vmid: 'twitter:title', content: title },
                { property: 'twitter:description', vmid: 'twitter:description', content: description },
            ],
        }
    },
    data() {
        return {
            options: onDemandQualityOptions,
            settingsService,
            modalService,
            selectedOption: '',
        }
    },

    computed: {
        memberId() {
            return this.$store.getters['member/currentMember'].id
        },
        currentOnDemandQualityKey() {
            return this.$store.state.settings.currentOnDemandQualityKey
        },
        isPremium() {
            return this.$store.getters['premium/isPremium']
        },
        hasEverBeenPremium() {
            return this.$store.getters['premium/hasEverBeenPremium']
        },
    },

    watch: {
        currentOnDemandQualityKey: {
            handler(newValue) {
                this.selectedOption = newValue
            },
            immediate: true,
        },
    },

    mounted() {
        this.getSubscriptions()
    },

    methods: {
        handleQualitySelection(event, qualityKey) {
            if (!qualityKey) return

            // Check if premium quality selected by non-premium user
            if(this.options[qualityKey].needsSub && !this.isPremium) {
                event.preventDefault()
                event.stopPropagation()
                // Reset to previous value
                this.$nextTick(() => {
                    this.selectedOption = this.currentOnDemandQualityKey
                })

                const isEntitledFreeTrial = !this.hasEverBeenPremium
                this.modalService.showPremiumUpsell({
                    headline: messages.PREMIUM_UPSELL_HEADLINE,
                    description: null,
                    subDescription: isEntitledFreeTrial ? messages.PREMIUM_UPSELL_SUBHEADLINE : null,
                    entitledFreeTrial: isEntitledFreeTrial,
                    primaryAction: {
                        text: isEntitledFreeTrial ? messages.PREMIUM_UPSELL_CTA_FREE_TRIAL : messages.PREMIUM_UPSELL_CTA_UPGRADE,
                        route: '/premium/view-plans',
                    },
                    secondaryAction: {
                        text: messages.PREMIUM_UPSELL_SECONDARY,
                        route: '/premium/view-plans',
                    },
                })
                return
            }

            this.save(qualityKey)
        },
        async getSubscriptions() {
            const premiumService = DataLayer.subscriptions
            const subscriptions = await premiumService.getSubscriptionsByMemberId(this.memberId)
            this.$store.commit('premium/SET_SUBSCRIPTIONS', subscriptions.body)
            this.selectedOption = this.currentOnDemandQualityKey
        },
        async save(qualityKey) {
            const savedQuality = await this.settingsService.setCurrentOnDemandQuality(qualityKey)
            if (!savedQuality) return
            this.$store.commit('settings/SET_SETTINGS', { currentOnDemandQualityKey: qualityKey })

            this.selectedOption = this.currentOnDemandQualityKey

            this.$store.dispatch('appearance/handleNotification', {
                message: this.$m('AUDIO_QUALITY_UPDATED'),
                type: 'success',
            })
        },
    },
}
</script>

<template>
    <BaseSection padded>
        <h6 :class="[$style.subtitle, $style.subtitleInfo]">Audio Quality</h6>

        <div :class="$style.qualitySelection">
            <div
                v-for="(value, name) in options"
                :key="'audioQuality' + name"
                :class="[$style.quality]"
            >
                <input
                    :id="'audioQuality' + name"
                    v-model="selectedOption"
                    :class="$style.radio"
                    :value="name"
                    type="radio"
                    name="audioQuality"
                    @click="handleQualitySelection($event, name)"
                >
                <div>
                    <label
                        :class="[$style.QualityLabel, $style.label]"
                        :for="'audioQuality' + name"
                    >{{
                        value.title
                    }}</label>
                    <label
                        :class="[$style.QualityLabel, $style.sublabel]"
                        :for="'audioQuality' + name"
                    >{{
                        value.subtitle
                    }}</label>
                </div>
            </div>
        </div>
        <div :class="$style.note">
            <p>Audio quality affects radio and on-demand streaming. Changing audio quality takes effect from the next playback.</p>
        </div>
    </BaseSection>
</template>

<style lang="scss" module>
@import '@design';

// %radio fills an unchecked control with $c-mirage-grey, which read fine when these options
// sat on the near-black page. They sit on a $c-steel-grey panel now, and the two are within
// two points of the same lightness — the control disappeared into it. Black against the
// panel instead. Overridden here rather than in _elements.scss because the reason is local:
// it is this panel the control has to be seen against.
.radio {
    @extend %radio;

    margin-right: $s-s;
    cursor: pointer;
    background: $c-black;
}

// Bold: the tier is the choice being made, and the line under it only explains it.
.label {
    @extend %contentSubtitleM;

    font-weight: 700;
    cursor: pointer;
}

.sublabel {
    @extend %contentSubtitleS;

    display: block;
    cursor: pointer;
}

.subtitle {
    margin-top: 0;
    text-align: center;
}

// The three options are one panel, not three loose rows (Figma 859:57) — and the same
// 16px card radius the External Players tab gives its channel rows, so the two Settings
// tabs agree. The padding is what keeps the `.quality ~ .quality` rule below reading as a
// divider BETWEEN rows rather than a border ON the panel: the border is drawn on the
// sibling row, so the panel's inset holds it clear of the rounded edge.
.qualitySelection {
    padding: $s-l;
    background: $c-steel-grey;
    border-radius: $size-radius-m;
}

.quality {
    display: flex;
    align-items: top;

    .label,
    .sublabel {
        color: lighten($c-text-faded, 20%);
    }

    ~ .quality {
        padding-top: $s-xl;
        margin-top: $s-xl;
        border-top: 1px solid transparentize($c-dusty-gray, 0.9);
    }

    &:hover {
        .label,
        .sublabel {
            color: $c-white;
        }
        .radio {
            background: transparentize($c-highlight, 0.5);
        }
    }
}

.note {
    @extend %contentSubtitleM;

    margin-top: $s-xxxl;

    p {
        margin: 0;
    }
}

@include tablet {
    .subtitle {
        text-align: left;
    }
}
</style>
